import { useState } from 'react';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { Plus, Search } from 'lucide-react';
import { getPractice } from 'library/auth';
import { blueCheckboxClass, choiceBtnUnselected } from 'components/Profile/helper';
import Option from './Option';
import ContactList from './ContactList';
import NewPerson from './NewPerson';
import { INITIAL_VALUES, NOT_LISTED, validationSchema, filterContacts } from './helper';

const FieldError = ({ name }) => (
  <ErrorMessage name={name} render={(msg) => <p className="mt-2 text-sm text-error">{msg}</p>} />
);

const AuthorizeForm = ({ contacts, onCancel, onSubmit = null }) => {
  const practiceName = getPractice()?.title || 'the practice';
  const [query, setQuery] = useState('');

  const familyMembers = filterContacts(contacts?.family_members, query);
  const emergencyContacts = filterContacts(contacts?.emergency_contacts, query);
  const noMatches = query.trim() && !familyMembers.length && !emergencyContacts.length;

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={validationSchema} onSubmit={(values) => onSubmit?.(values)}>
      {({ values, isSubmitting }) => (
        <Form className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 sm:px-6">
            <div role="radiogroup" aria-label="Person to authorize" className="overflow-hidden rounded-2xl border border-gray-200">
              <label className="flex items-center gap-3 border-b border-gray-100 px-4 sm:px-5">
                <Search size={18} className="shrink-0 text-secondary-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, relationship or phone"
                  className="h-12 w-full min-w-0 bg-transparent text-sm text-secondary-900 outline-none placeholder:text-secondary-400"
                />
              </label>

              <div className="max-h-64 overflow-y-auto">
                <ContactList title="Family members" personType="Patient" contacts={familyMembers} />
                <ContactList title="Emergency contacts" personType="EmergencyContact" contacts={emergencyContacts} />

                {noMatches && (
                  <p className="px-4 py-4 text-center text-sm text-secondary-400 sm:px-5">No matching contacts</p>
                )}
              </div>

              <div className="border-t border-gray-100">
                <Option
                  value={NOT_LISTED}
                  avatar={
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-dashed border-gray-300 text-secondary-400">
                      <Plus size={18} />
                    </span>
                  }
                  title="Someone not listed"
                />

                {values.person_id === NOT_LISTED && <NewPerson />}
              </div>
            </div>

            <FieldError name="person_id" />

            <label className="mt-4 flex cursor-pointer items-start gap-3">
              <Field
                type="checkbox"
                name="agreed"
                className={`checkbox checkbox-sm mt-0.5 shrink-0 rounded-md border-gray-300 [--chkfg:white] ${blueCheckboxClass}`}
              />
              <span className="text-sm leading-relaxed text-secondary-600">
                I authorize {practiceName} to discuss my appointments, billing and treatment with the person I’ve selected.
                I can revoke this at any time.
              </span>
            </label>

            <FieldError name="agreed" />
          </div>

          <div className="flex shrink-0 justify-end gap-3 px-5 pb-5 pt-4 sm:px-6 sm:pb-6">
            <button type="button" onClick={onCancel} disabled={isSubmitting} className={`btn h-11 min-h-0 rounded-xl px-5 font-semibold ${choiceBtnUnselected}`}>
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="btn h-11 min-h-0 rounded-xl border-none bg-teamcare-blue px-6 font-semibold text-white hover:bg-teamcare-blue-dark disabled:bg-teamcare-blue disabled:text-white disabled:opacity-60"
            >
              {isSubmitting ? <span className="loading loading-spinner loading-sm" /> : 'Authorize'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default AuthorizeForm;
